import { useEffect, useState } from 'react';
import { fetchCohorts, fetchCohortsRegion, fetchCohortTrend } from '../lib/api';
import { RetentionCurve, ChannelBars, TrendLine } from './Charts';

const fmt = n => n == null ? '·' : n.toLocaleString('en-IN');

export default function RetentionTab({ filters, entityKey }) {
  const [cohorts, setCohorts] = useState(null);
  const [trend, setTrend] = useState(null);
  const [selectedCohort, setSelectedCohort] = useState('AUTO');
  const [busy, setBusy] = useState(true);

  const region = filters.region && filters.region !== 'ALL' ? filters.region : null;

  useEffect(() => {
    (async () => {
      setBusy(true);
      const [c, t] = await Promise.all([
        region
          ? fetchCohortsRegion({ category: filters.category, region })   // region-wise (all brands)
          : fetchCohorts({ category: filters.category, entityKey }),
        fetchCohortTrend(filters.category),
      ]);
      setCohorts(c.data); setTrend(t.data); setBusy(false);
    })();
  }, [filters.category, entityKey, region]);

  if (busy || !cohorts) return <div className="loading">Loading retention…</div>;

  const ch = filters.channel;
  const channelCohorts = cohorts[ch] || [];
  const pickAuto = () => {
    for (let i = channelCohorts.length-1; i >= 0; i--) {
      if (channelCohorts[i].cells?.[5]) return channelCohorts[i];
    }
    for (let i = channelCohorts.length-1; i >= 0; i--) {
      if (channelCohorts[i].cells?.[3]) return channelCohorts[i];
    }
    return channelCohorts[channelCohorts.length-1];
  };
  const anchor = selectedCohort === 'AUTO'
    ? pickAuto()
    : channelCohorts.find(c => c.label === selectedCohort);

  const cellsAt = mi => anchor?.cells?.[mi];
  const kpis = [
    { label: `${anchor?.label || '·'} · Cohort size`, val: fmt(anchor?.size) },
    { label: `${anchor?.label || '·'} · M1`, val: cellsAt(1) ? `${cellsAt(1).rate}%` : 'n/a' },
    { label: `${anchor?.label || '·'} · M3`, val: cellsAt(3) ? `${cellsAt(3).rate}%` : 'n/a' },
    { label: `${anchor?.label || '·'} · M5`, val: cellsAt(5) ? `${cellsAt(5).rate}%` : 'n/a' },
  ];

  // channel bars: same anchor cohort (by month) across every channel.
  // Use the anchor's cohort_month so we compare a MATURE cohort, not the newest.
  const anchorLabel = anchor?.label;
  const channelBarData = ['Omni','Retail','E-Commerce'].reduce((acc, chan) => {
    const list = cohorts[chan] || [];
    const c = list.find(x => x.label === anchorLabel);
    acc[chan] = { m1: c?.cells?.[1]?.rate, m3: c?.cells?.[3]?.rate };
    return acc;
  }, {});

  return (
    <>
      <div className="explainer">
        All four KPI cards below describe the <b>same cohort</b> at different maturity points (M1, M3, M5).
        Change the anchor cohort using the picker below. <br />
        <span style={{ color: 'var(--muted)' }}>
          Retail cohort = anyone who bought Retail in their first month. E-Comm cohort = anyone who bought E-Comm.
          Omni cohort = customers who bought both channels (subset of both). Retention at Mn counts any purchase in any channel.
        </span>
      </div>

      <div className="panel" style={{ display:'flex', alignItems:'center', gap: 16 }}>
        <span className="filter-label">Cohort</span>
        <select className="filter-select" value={selectedCohort} onChange={e => setSelectedCohort(e.target.value)}>
          <option value="AUTO">Auto · latest fully mature</option>
          {channelCohorts.map(c => <option key={c.label} value={c.label}>{c.label}</option>)}
        </select>
        <span style={{ fontFamily:'var(--font-mono)', fontSize:12, color:'var(--muted)' }}>
          Auto-picks the most recent cohort with M5 data.
        </span>
      </div>

      <div className="kpi-strip">
        {kpis.map(k => (
          <div className="kpi" key={k.label}>
            <div className="label">{k.label}</div>
            <div className="val">{k.val}</div>
          </div>
        ))}
      </div>

      <div className="panel-grid">
        <div className="panel">
          <div className="panel-head">
            <div>
              <div className="panel-title">Retention curve</div>
              <div className="panel-sub">Same anchor cohort as the KPIs above, tracked M0 through M5</div>
            </div>
          </div>
          {anchor && <RetentionCurve cells={anchor.cells} />}
        </div>
        <div className="panel">
          <div className="panel-head">
            <div>
              <div className="panel-title">M1 / M3 by channel</div>
              <div className="panel-sub">Same anchor cohort, broken down by channel</div>
            </div>
          </div>
          <ChannelBars channels={channelBarData} />
        </div>
      </div>

      {trend && (
        <>
          <div className="panel panel-lead">
            <div className="panel-head">
              <div>
                <div className="panel-title">M1 trend by cohort <span style={{color:'var(--orange)'}}>· the drift story</span></div>
                <div className="panel-sub">Every cohort's M0→M1 conversion. Reading left to right shows direction of the business.</div>
              </div>
            </div>
            <TrendLine trend={trend} metricKey="m1_pct" />
          </div>
          <div className="panel panel-lead">
            <div className="panel-head">
              <div>
                <div className="panel-title">M3 trend by cohort <span style={{color:'var(--orange)'}}>· the durability story</span></div>
                <div className="panel-sub">Every mature cohort's M3 retention. Compounds through M6, M9, M12.</div>
              </div>
            </div>
            <TrendLine trend={trend} metricKey="m3_pct" />
          </div>
        </>
      )}
    </>
  );
}
